import {
  useAddCoursePlanComment,
  useApproveCoursePlan,
  useDeleteCoursePlanComment,
  useGetCoursePlan,
  useListCoursePlanReviews,
  useReopenCoursePlan,
  useRequestCoursePlanUpdate,
  useResolveCoursePlanUpdateRequest,
} from '@actions';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import Button from '@components/V4/Button';
import Textarea from '@components/V4/Inputs/Textarea';
import Select from '@components/V4/Select/Select';
import { useAuthState } from '@contexts/AuthContext';
import {
  AnnotationIcon,
  CheckCircleIcon,
  ExclamationIcon,
  RefreshIcon,
  TrashIcon,
  XIcon,
} from '@heroicons/react/outline';
import { EmptyState, FilterField, SectionHeading } from '@modules/Admin/Reporting/common';
import {
  CoursePlanCommentDTO,
  CoursePlanCommentRef,
  CoursePlanContentDTO,
  CoursePlanDTO,
  CoursePlanReviewStatus,
  CoursePlanSummaryDTO,
} from '@shared/apis/coursePlans.apis';
import { UserRole } from '@shared/enums';
import useToast from 'apps/agora/src/hooks/useToast';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import PlanView, { commentRefKey } from '../CoursePlanGenerator/PlanView';

const REVIEW_STYLES: Record<CoursePlanReviewStatus, { label: string; className: string }> = {
  in_review: { label: 'In review', className: 'bg-white/10 text-customLight' },
  update_requested: { label: 'Update requested', className: 'bg-customGold/15 text-customGold' },
  revising: { label: 'AI revising…', className: 'bg-customLila/15 text-customLila' },
  approved: { label: 'Approved', className: 'bg-customGreen/15 text-customGreen' },
};

const STATUS_FILTERS: { label: string; value: CoursePlanReviewStatus | 'all' }[] = [
  { label: 'All statuses', value: 'all' },
  { label: 'In review', value: 'in_review' },
  { label: 'Update requested', value: 'update_requested' },
  { label: 'AI revising', value: 'revising' },
  { label: 'Approved', value: 'approved' },
];

const errorMessage = (error: unknown, fallback: string) =>
  (error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
    ?.message ?? fallback;

const ReviewBadge = (props: { status?: CoursePlanReviewStatus }) => {
  const style = REVIEW_STYLES[props.status ?? 'in_review'];
  return (
    <span
      className={mergeClassNames(
        'w-fit rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase',
        style.className
      )}
    >
      {style.label}
    </span>
  );
};

const refOptions = (plan: CoursePlanContentDTO) => {
  const options: { label: string; value: string; ref: CoursePlanCommentRef }[] = [
    { label: 'Overview', value: 'overview::', ref: { section: 'overview', label: 'Overview' } },
    {
      label: 'Prerequisites',
      value: 'prerequisites::',
      ref: { section: 'prerequisites', label: 'Prerequisites' },
    },
    {
      label: 'Learning outcomes',
      value: 'learningOutcomes::',
      ref: { section: 'learningOutcomes', label: 'Learning outcomes' },
    },
  ];
  plan.modules.forEach((module, moduleIndex) => {
    options.push({
      label: `Module ${moduleIndex + 1}: ${module.title}`,
      value: `modules:${moduleIndex}:`,
      ref: {
        section: 'modules',
        moduleIndex,
        label: `Module ${moduleIndex + 1}: ${module.title}`,
      },
    });
    module.lessons.forEach((lesson, lessonIndex) => {
      options.push({
        label: `   Lesson ${moduleIndex + 1}.${lessonIndex + 1}: ${lesson.title}`,
        value: `modules:${moduleIndex}:${lessonIndex}`,
        ref: {
          section: 'modules',
          moduleIndex,
          lessonIndex,
          label: `Lesson ${moduleIndex + 1}.${lessonIndex + 1}: ${lesson.title}`,
        },
      });
    });
  });
  [
    ['assessment', 'Assessment'],
    ['mentorGuidelines', 'Mentor guidelines'],
    ['gapsInCurrentContent', 'Gaps in current content'],
    ['changesVsCurrent', 'Changes versus current course'],
  ].forEach(([section, label]) =>
    options.push({ label, value: `${section}::`, ref: { section, label } })
  );
  return options;
};

const CommentCard = (props: {
  comment: CoursePlanCommentDTO;
  canDelete: boolean;
  onDelete: () => void;
}) => {
  const { comment, canDelete, onDelete } = props;
  const addressed = !!comment.addressedInVersion;
  return (
    <div
      className={mergeClassNames(
        'flex flex-col gap-1 rounded-xl px-4 py-3',
        addressed ? 'bg-white/5 opacity-70' : 'bg-customGold/10'
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-xs font-semibold text-customGold">{comment.ref.label}</span>
        {canDelete && !addressed ? (
          <button
            type="button"
            className="text-customGrey hover:text-customRed"
            title="Delete comment"
            onClick={onDelete}
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        ) : null}
      </div>
      <p className="whitespace-pre-wrap text-sm text-customLight">{comment.text}</p>
      <span className="text-xs text-customGrey">
        {comment.author.fullName ?? 'Unknown'} ·{' '}
        {moment(comment.createdAt).format('D MMM YYYY HH:mm')}
        {addressed ? ` · addressed in v${comment.addressedInVersion}` : ''}
      </span>
    </div>
  );
};

const PlanRow = (props: {
  plan: CoursePlanSummaryDTO;
  isSelected: boolean;
  onSelect: () => void;
}) => {
  const { plan, isSelected, onSelect } = props;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(event) => (event.key === 'Enter' ? onSelect() : undefined)}
      className={mergeClassNames(
        'flex cursor-pointer flex-col gap-1 rounded-xl px-4 py-3 transition-colors',
        isSelected ? 'bg-white/10' : 'hover:bg-white/5'
      )}
    >
      <span className="truncate text-sm font-semibold text-white">
        {plan.planTitle ?? plan.course.title}
      </span>
      <span className="truncate text-xs text-customGrey">
        {plan.course.title} · v{plan.version} ·{' '}
        {moment(plan.finishedAt ?? plan.createdAt).format('D MMM YYYY')}
      </span>
      <div className="flex flex-wrap items-center gap-2">
        <ReviewBadge status={plan.review?.status} />
        {plan.commentCount ? (
          <span className="inline-flex items-center gap-1 text-[11px] text-customGrey">
            <AnnotationIcon className="h-3.5 w-3.5" />
            {plan.openCommentCount} open / {plan.commentCount}
          </span>
        ) : null}
      </div>
    </div>
  );
};

const ReviewDetail = (props: { plan: CoursePlanDTO; onChanged: () => void }) => {
  const { plan, onChanged } = props;
  const toast = useToast();
  const { roles, userId } = useAuthState();
  const isSuperUser = roles.includes(UserRole.SuperUser);
  const status = plan.review?.status ?? 'in_review';

  const [refValue, setRefValue] = useState<string>('overview::');
  const [text, setText] = useState('');
  const [note, setNote] = useState('');
  const [declineNote, setDeclineNote] = useState('');
  const [showDecline, setShowDecline] = useState(false);
  const [viewVersion, setViewVersion] = useState<number>();

  const options = useMemo(() => (plan.plan ? refOptions(plan.plan) : []), [plan.plan]);
  const openComments = (plan.comments ?? []).filter((comment) => !comment.addressedInVersion);
  const addressedComments = (plan.comments ?? []).filter((comment) => comment.addressedInVersion);
  const commentCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    openComments.forEach((comment) => {
      const key = commentRefKey(comment.ref);
      counts[key] = (counts[key] ?? 0) + 1;
    });
    return counts;
  }, [openComments]);

  const mutationOptions = (success: string, fallback: string) => ({
    onSuccess: () => {
      toast.success(success);
      onChanged();
    },
    onError: (error: unknown) => toast.error(errorMessage(error, fallback)),
  });

  const { mutate: addComment, isLoading: isAdding } = useAddCoursePlanComment(plan._id, {
    ...mutationOptions('Comment added.', 'Could not add the comment.'),
    onSuccess: () => {
      setText('');
      toast.success('Comment added.');
      onChanged();
    },
  });
  const { mutate: deleteComment } = useDeleteCoursePlanComment(
    plan._id,
    mutationOptions('Comment deleted.', 'Could not delete the comment.')
  );
  const { mutate: requestUpdate, isLoading: isRequesting } = useRequestCoursePlanUpdate(plan._id, {
    ...mutationOptions(
      'Update requested. A super user has to approve it.',
      'Could not request the update.'
    ),
    onSuccess: () => {
      setNote('');
      toast.success('Update requested. A super user has to approve it.');
      onChanged();
    },
  });
  const { mutate: resolveRequest, isLoading: isResolving } = useResolveCoursePlanUpdateRequest(
    plan._id,
    {
      ...mutationOptions('Done.', 'Could not resolve the request.'),
      onSuccess: () => {
        setShowDecline(false);
        setDeclineNote('');
        toast.success('Done.');
        onChanged();
      },
    }
  );
  const { mutate: approvePlan, isLoading: isApproving } = useApproveCoursePlan(
    plan._id,
    mutationOptions('Plan approved.', 'Could not approve the plan.')
  );
  const { mutate: reopenPlan, isLoading: isReopening } = useReopenCoursePlan(
    plan._id,
    mutationOptions('Plan reopened for review.', 'Could not reopen the plan.')
  );

  const selectedRef = options.find((option) => option.value === refValue)?.ref;
  const canComment = status === 'in_review' || status === 'update_requested';
  const shownRevision = plan.revisions?.find((revision) => revision.version === viewVersion);

  if (!plan.plan) return <EmptyState>This run has no plan to review.</EmptyState>;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3 rounded-2xl bg-surfaceObject p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <span className="text-xs uppercase tracking-wide text-customGrey">
              {plan.course.title}
            </span>
            <span className="text-lg font-bold text-white">
              {plan.plan.title} <span className="text-customGrey">v{plan.version}</span>
            </span>
            <div className="flex flex-wrap items-center gap-2 text-xs text-customGrey">
              <ReviewBadge status={status} />
              <span>
                Generated {moment(plan.finishedAt ?? plan.createdAt).format('D MMM YYYY')}
                {plan.createdBy?.fullName ? ` by ${plan.createdBy.fullName}` : ''}
                {plan.model ? ` · ${plan.model}` : ''}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {status === 'approved' ? (
              isSuperUser ? (
                <Button
                  variant="secondary"
                  buttonText="Reopen for review"
                  icon={<RefreshIcon className="h-4 w-4" />}
                  isLoading={isReopening}
                  disabled={isReopening}
                  onClick={() => reopenPlan({})}
                />
              ) : null
            ) : status === 'in_review' ? (
              <Button
                buttonText="Approve this version"
                icon={<CheckCircleIcon className="h-4 w-4" />}
                isLoading={isApproving}
                disabled={isApproving}
                onClick={() => approvePlan({})}
              />
            ) : null}
          </div>
        </div>

        {status === 'approved' && plan.review?.approvedBy ? (
          <span className="text-sm text-customGreen">
            Approved by {plan.review.approvedBy.fullName ?? 'an admin'}{' '}
            {moment(plan.review.approvedAt).fromNow()}. Mentors can work from this version.
          </span>
        ) : null}

        {status === 'revising' ? (
          <div className="flex items-center gap-3 text-sm text-customLila">
            <LoadingSpinner size="normal" />
            The AI is applying {openComments.length} comment
            {openComments.length === 1 ? '' : 's'}
            {plan.review?.requestNote ? ' and the request note' : ''}. This takes a few minutes; the
            page refreshes on its own.
          </div>
        ) : null}

        {status === 'update_requested' ? (
          <div className="flex flex-col gap-3 rounded-xl bg-customGold/10 p-4">
            <div className="flex items-start gap-2 text-sm text-customGold">
              <ExclamationIcon className="mt-0.5 h-5 w-5 shrink-0" />
              <div className="flex flex-col gap-1">
                <span className="font-semibold">
                  Update requested by {plan.review?.requestedBy?.fullName ?? 'an admin'}{' '}
                  {moment(plan.review?.requestedAt).fromNow()} · {openComments.length} open comment
                  {openComments.length === 1 ? '' : 's'}
                </span>
                {plan.review?.requestNote ? (
                  <span className="whitespace-pre-wrap text-customLight">
                    {plan.review.requestNote}
                  </span>
                ) : null}
                {plan.review?.revisionError ? (
                  <span className="text-customRed">
                    The last revision attempt failed: {plan.review.revisionError}
                  </span>
                ) : null}
              </div>
            </div>
            {isSuperUser ? (
              <div className="flex flex-col gap-3">
                <div className="flex flex-wrap gap-2">
                  <Button
                    buttonText={
                      plan.review?.revisionError ? 'Retry AI revision' : 'Approve & run AI revision'
                    }
                    icon={<CheckCircleIcon className="h-4 w-4" />}
                    isLoading={isResolving}
                    disabled={isResolving}
                    onClick={() => resolveRequest({ decision: 'approve' })}
                  />
                  <Button
                    variant="secondary"
                    buttonText="Decline request"
                    icon={<XIcon className="h-4 w-4" />}
                    disabled={isResolving}
                    onClick={() => setShowDecline((value) => !value)}
                  />
                </div>
                {showDecline ? (
                  <div className="flex flex-col gap-2">
                    <Textarea
                      id="decline-note"
                      name="declineNote"
                      label="Why is this declined? (shown to the reviewers)"
                      rows={2}
                      value={declineNote}
                      onChange={(event) => setDeclineNote(event.target.value)}
                    />
                    <div>
                      <Button
                        variant="secondary"
                        buttonText="Confirm decline"
                        isLoading={isResolving}
                        disabled={isResolving}
                        onClick={() => resolveRequest({ decision: 'decline', note: declineNote })}
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            ) : (
              <span className="text-xs text-customGrey">
                Waiting for a super user to approve the request. You can still add comments; they
                will be included in the revision.
              </span>
            )}
          </div>
        ) : null}

        {status === 'in_review' && plan.review?.declineNote ? (
          <span className="text-sm text-customRed">
            The last update request was declined: {plan.review.declineNote}
          </span>
        ) : null}

        {plan.revisionNotes?.length ? (
          <div className="flex flex-col gap-1 rounded-xl bg-white/5 p-4">
            <span className="text-xs font-semibold uppercase tracking-wide text-customGrey">
              What changed in v{plan.version}
            </span>
            <ul className="list-disc pl-5 text-sm text-customLight">
              {plan.revisionNotes.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-6 laptop:grid-cols-[1fr_360px]">
        <div className="flex min-w-0 flex-col gap-4">
          {plan.revisions?.length ? (
            <div className="flex flex-wrap items-center gap-2 text-xs text-customGrey">
              <span>Version:</span>
              <button
                type="button"
                className={mergeClassNames(
                  'rounded-full px-3 py-1 font-semibold',
                  !viewVersion ? 'bg-customPrimary text-black' : 'bg-white/10 text-customLight'
                )}
                onClick={() => setViewVersion(undefined)}
              >
                v{plan.version} (current)
              </button>
              {[...plan.revisions]
                .sort((a, b) => b.version - a.version)
                .map((revision) => (
                  <button
                    key={revision.version}
                    type="button"
                    className={mergeClassNames(
                      'rounded-full px-3 py-1 font-semibold',
                      viewVersion === revision.version
                        ? 'bg-customPrimary text-black'
                        : 'bg-white/10 text-customLight'
                    )}
                    onClick={() => setViewVersion(revision.version)}
                  >
                    v{revision.version}
                  </button>
                ))}
            </div>
          ) : null}
          {shownRevision ? (
            <>
              <div className="rounded-xl bg-white/5 p-4 text-xs text-customGrey">
                Read-only snapshot of v{shownRevision.version}, replaced{' '}
                {moment(shownRevision.revisedAt).format('D MMM YYYY HH:mm')}
                {shownRevision.requestedBy?.fullName
                  ? ` after a request by ${shownRevision.requestedBy.fullName}`
                  : ''}
                .
              </div>
              <PlanView plan={shownRevision.plan} />
            </>
          ) : (
            <PlanView
              plan={plan.plan}
              research={plan.research?.markdown}
              hooks={
                canComment
                  ? {
                      commentCounts,
                      onComment: (ref) => {
                        setRefValue(
                          `${ref.section}:${ref.moduleIndex ?? ''}:${ref.lessonIndex ?? ''}`
                        );
                        document
                          .getElementById('course-plan-comment-text')
                          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        document.getElementById('course-plan-comment-text')?.focus();
                      },
                    }
                  : { commentCounts }
              }
            />
          )}
        </div>

        <div className="flex flex-col gap-4">
          {canComment ? (
            <div className="flex flex-col gap-3 rounded-2xl bg-surfaceObject p-4">
              <SectionHeading title="Add a comment" />
              <FilterField width="w-full">
                <Select
                  label="About"
                  variant="secondary"
                  showSearch
                  value={refValue}
                  options={options.map((option) => ({ label: option.label, value: option.value }))}
                  onSelect={(value) => setRefValue(String(value))}
                />
              </FilterField>
              <Textarea
                id="course-plan-comment-text"
                name="comment"
                label="Comment"
                placeholder="What should change here, and why?"
                rows={4}
                value={text}
                onChange={(event) => setText(event.target.value)}
              />
              <div>
                <Button
                  buttonText="Add comment"
                  icon={<AnnotationIcon className="h-4 w-4" />}
                  isLoading={isAdding}
                  disabled={isAdding || !text.trim() || !selectedRef}
                  onClick={() => selectedRef && addComment({ text, ref: selectedRef })}
                />
              </div>
            </div>
          ) : null}

          <div className="flex flex-col gap-3 rounded-2xl bg-surfaceObject p-4">
            <SectionHeading title={`Open comments (${openComments.length})`} />
            {!openComments.length ? (
              <span className="text-sm text-customGrey">
                No open comments. Use the "Comment" buttons on the plan or the form above.
              </span>
            ) : (
              openComments.map((comment) => (
                <CommentCard
                  key={comment._id}
                  comment={comment}
                  canDelete={isSuperUser || comment.author._id === userId}
                  onDelete={() => deleteComment({ commentId: comment._id })}
                />
              ))
            )}
          </div>

          {status === 'in_review' ? (
            <div className="flex flex-col gap-3 rounded-2xl bg-surfaceObject p-4">
              <SectionHeading
                title="Request an update"
                description="The AI rewrites the plan from the open comments and this note. A super user approves the request first."
              />
              <Textarea
                id="course-plan-request-note"
                name="note"
                label="Note for the revision (optional)"
                placeholder="e.g. Keep the total at 20 sessions and move interview prep earlier."
                rows={3}
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
              <div>
                <Button
                  buttonText="Request update"
                  icon={<RefreshIcon className="h-4 w-4" />}
                  isLoading={isRequesting}
                  disabled={isRequesting || (!openComments.length && !note.trim())}
                  onClick={() => requestUpdate({ note: note || undefined })}
                />
              </div>
            </div>
          ) : null}

          {addressedComments.length ? (
            <div className="flex flex-col gap-3 rounded-2xl bg-surfaceObject p-4">
              <SectionHeading title={`Addressed comments (${addressedComments.length})`} />
              {[...addressedComments]
                .sort((a, b) => (b.addressedInVersion ?? 0) - (a.addressedInVersion ?? 0))
                .map((comment) => (
                  <CommentCard
                    key={comment._id}
                    comment={comment}
                    canDelete={false}
                    onDelete={() => undefined}
                  />
                ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const CoursePlanReviews = () => {
  const history = useHistory();
  const location = useLocation();
  const queryPlanId = new URLSearchParams(location.search).get('plan') ?? undefined;

  const [statusFilter, setStatusFilter] = useState<CoursePlanReviewStatus | 'all'>('all');
  const [selectedId, setSelectedId] = useState<string | undefined>(queryPlanId);

  const {
    data: plans,
    isLoading,
    refetch: refetchList,
  } = useListCoursePlanReviews({ refetchOnWindowFocus: false });

  const { data: selected, refetch: refetchSelected } = useGetCoursePlan(selectedId ?? '', {
    enabled: !!selectedId,
    refetchOnWindowFocus: false,
  });

  const isRevising = selected?.review?.status === 'revising';
  useEffect(() => {
    if (!isRevising) return;
    const timer = setInterval(() => {
      refetchSelected();
      refetchList();
    }, 5000);
    return () => clearInterval(timer);
  }, [isRevising, refetchSelected, refetchList]);

  const filtered = useMemo(
    () =>
      (plans ?? []).filter(
        (plan) => statusFilter === 'all' || (plan.review?.status ?? 'in_review') === statusFilter
      ),
    [plans, statusFilter]
  );

  useEffect(() => {
    if (!filtered.length) return;
    if (!selectedId || !filtered.some((plan) => plan._id === selectedId)) {
      setSelectedId(filtered[0]._id);
    }
  }, [filtered, selectedId]);

  const select = (id: string) => {
    setSelectedId(id);
    history.replace({ search: `?plan=${id}` });
  };

  const refresh = () => {
    refetchList();
    refetchSelected();
  };

  return (
    <div className="flex flex-col gap-8 p-8 font-raleway text-white">
      <h1 className="text-2xl font-bold">Course Plans</h1>

      <div className="grid grid-cols-1 gap-6 laptop:grid-cols-[320px_1fr]">
        <div className="flex flex-col gap-3 rounded-2xl bg-surfaceObject p-3">
          <div className="flex items-center justify-between px-2 pt-1">
            <span className="text-sm font-semibold text-white">Generated plans</span>
            <button
              type="button"
              className="text-customGrey hover:text-white"
              title="Refresh"
              onClick={refresh}
            >
              <RefreshIcon className="h-4 w-4" />
            </button>
          </div>
          <FilterField width="w-full">
            <Select
              variant="secondary"
              value={statusFilter}
              options={STATUS_FILTERS}
              onSelect={(value) => setStatusFilter(value as CoursePlanReviewStatus | 'all')}
            />
          </FilterField>
          {isLoading ? (
            <div className="flex h-24 items-center justify-center">
              <LoadingSpinner size="normal" />
            </div>
          ) : !filtered.length ? (
            <span className="px-2 pb-2 text-sm text-customGrey">
              No plans here yet. A super user generates them from Tools → Course Plan Generator.
            </span>
          ) : (
            filtered.map((plan) => (
              <PlanRow
                key={plan._id}
                plan={plan}
                isSelected={plan._id === selectedId}
                onSelect={() => select(plan._id)}
              />
            ))
          )}
        </div>

        <div className="min-w-0">
          {!selectedId ? (
            <EmptyState>Select a plan to review it.</EmptyState>
          ) : !selected ? (
            <div className="flex h-48 items-center justify-center">
              <LoadingSpinner size="normal" />
            </div>
          ) : (
            <ReviewDetail plan={selected} onChanged={refresh} />
          )}
        </div>
      </div>
    </div>
  );
};

export default CoursePlanReviews;
